import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, from, map, of } from 'rxjs';
import { AuthSession } from '../models/profile';
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { parseAdGroupsClaim } from '../utils/ad-groups.util';

const AUTHLOCALSTORAGETOKEN = `azUser`;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentUser$: Observable<any>;
  currentUserSubject: BehaviorSubject<any>;

  get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  constructor() {
    this.currentUserSubject = new BehaviorSubject<any>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    const existingAuth = this.getAuthFromLocalStorage();
    if (existingAuth) {
      this.currentUserSubject.next(existingAuth);
    }
  }

  loginSSO(provider: string): Observable<any> {
    return this.amplifyLoginSSO(provider).pipe(
      map((loginResult: any) => {
        console.log('Login result: ', JSON.stringify(loginResult));
        return loginResult;
      }),
      catchError((err) => {
        console.log('Error logging in', err);
        throw new Error(err.message);
      })
    );
  }

  logout() {
    this.amplifyLogout();
    localStorage.removeItem(AUTHLOCALSTORAGETOKEN);
    this.currentUserSubject.next(null);
  }

  getUserBySession(): Observable<AuthSession | undefined> {
    return this.amplifyGetUserSession().pipe(
      map((user: any) => {
        if (user && user.tokens) {
          const result = this.getUserAuthDataMap(user);
          this.setAuthFromLocalStorage(result);
          this.currentUserSubject.next(result);
          return result;
        } else {
          return undefined;
        }
      }),
      catchError((err) => {
        console.error('Error getting user session:', err);
        return of(undefined);
      })
    );
  }

  getAuthFromLocalStorage(): AuthSession | undefined {
    try {
      const lsValue = localStorage.getItem(AUTHLOCALSTORAGETOKEN);
      if (!lsValue) {
        return undefined;
      }
      const authData = JSON.parse(lsValue) as AuthSession;

      // Backward-compatible normalization for previously stored values
      if (authData?.payload) {
        authData.payload.groups = parseAdGroupsClaim(authData.payload.groups as any);
        authData.payload.adgroups = parseAdGroupsClaim(authData.payload.adgroups as any);
        if (!authData.payload.prid && authData.session?.idToken?.payload) {
          const tokenPayload = authData.session.idToken.payload;
          const storedPrid = tokenPayload['custom:prid'];
          authData.payload.prid =
            typeof storedPrid === 'string'
              ? storedPrid
              : this.extractPridFromUsername(tokenPayload['username']);
        }
      }

      return authData;
    } catch (error) {
      return undefined;
    }
  }

  setAuthFromLocalStorage(auth: AuthSession | undefined): boolean {
    if (auth && auth.session) {
      localStorage.setItem(AUTHLOCALSTORAGETOKEN, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getUserAuthDataMap(loginResult: any): AuthSession {
    const tokenPayload = loginResult.tokens.idToken.payload;
    const usernamePrid = this.extractPridFromUsername(tokenPayload['username']);

    const result: AuthSession = {
      userId: loginResult.userSub,
      session: loginResult.tokens,
      payload: {
        sub: tokenPayload.sub,
        firstname: tokenPayload.given_name,
        lastname: tokenPayload.middle_name,
        email: tokenPayload.email,
        email_verified: tokenPayload.email_verified,
        prid: tokenPayload['custom:prid'] ?? usernamePrid,
        groups: parseAdGroupsClaim(tokenPayload['cognito:groups']),
        adgroups: parseAdGroupsClaim(tokenPayload['custom:adgroups']),
        exp: tokenPayload.exp ?? 0,
      },
    };
    return result;
  }

  private extractPridFromUsername(username: unknown): string | undefined {
    if (typeof username !== 'string') {
      return undefined;
    }
    const trimmed = username.trim();
    if (!trimmed) {
      return undefined;
    }
    const match = /^AzureAD_(.+)@astrazeneca\.net$/i.exec(trimmed);
    return match?.[1] ?? undefined;
  }

  getPrid(): string | null {
    const currentPrid = this.currentUserValue?.payload?.prid;
    if (currentPrid) {
      return currentPrid;
    }
    const storedAuth = this.getAuthFromLocalStorage();
    return storedAuth?.payload?.prid ?? null;
  }

  // http auth service
  amplifyLoginSSO(provider: string): Observable<any> {
    try {
      const authResult = signInWithRedirect({
        provider: {
          custom: provider,
        },
      });
      return from(authResult);
    } catch (error) {
      throw new Error('AZ-AUTH-LOGIN-ERROR: ' + error);
    }
  }

  /**
   * Logs the user in with the username and password
   * @param email
   * @param password
   */
  amplifyLogout(): Observable<any> {
    try {
      const logoutResult = signOut();
      return from(logoutResult);
    } catch (error) {
      throw new Error('AZ-AUTH-LOGOUT-ERROR: ' + error);
    }
  }

  amplifyGetUserSession(): Observable<any> {
    try {
      const authResult = fetchAuthSession();
      return from(authResult);
    } catch (error) {
      throw new Error('AZ-AUTH-GETUSER-ERROR: ' + error);
    }
  }
}
